import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { authAPI } from '../../api';

const ProfileContainer = styled.div`
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  padding: 30px;
`;

const ProfileSection = styled.div`
  margin-bottom: 30px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  margin-bottom: 20px;
  color: #333;
  border-bottom: 1px solid #eee;
  padding-bottom: 10px;
`;

const ProfileInfo = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const InfoItem = styled.div`
  margin-bottom: 15px;
`;

const Label = styled.div`
  font-size: 14px;
  color: #666;
  margin-bottom: 5px;
`;

const Value = styled.div`
  font-size: 16px;
  color: #333;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  
  &:focus {
    border-color: #ef4444;
    outline: none;
  }
`;

const Button = styled.button`
  background-color: #ef4444;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background-color: #dc2626;
  }
  
  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #ef4444;
  font-size: 14px;
  margin-top: 5px;
`;

const SuccessMessage = styled.div`
  color: #10b981;
  font-size: 14px;
  margin-top: 5px;
`;

const Avatar = styled.div`
  width: 100px;
  height: 100px;
  background-color: #ef4444;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: bold;
  color: white;
  margin-bottom: 20px;
`;

const Profile: React.FC = () => {
  const { user, updateUserInfo } = useAuth();
  const [nickname, setNickname] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  
  // 초기 데이터 설정
  useEffect(() => {
    if (user) {
      setNickname(user.nickname || '');
      setEmail(user.email || '');
    }
  }, [user]);
  
  // 폼 제출 처리
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);
    
    try {
      // 변경된 사항만 전송
      const updateData: any = {};
      
      if (nickname !== user?.nickname) {
        updateData.nickname = nickname;
      }
      
      if (email !== user?.email) {
        updateData.email = email;
      }
      
      // 변경사항이 없는 경우
      if (Object.keys(updateData).length === 0) {
        setError('변경된 정보가 없습니다.');
        setLoading(false);
        return;
      }
      
      // 프로필 업데이트 요청
      const response = await authAPI.updateProfile(updateData);
      const responseData = response.data;
      
      if (responseData && responseData.success) {
        setSuccess('프로필 정보가 성공적으로 업데이트되었습니다.');
        // 사용자 정보 갱신
        updateUserInfo();
      } else {
        setError(responseData?.message || '프로필 정보 업데이트에 실패했습니다.');
      }
    } catch (err: any) {
      setError(err.message || '오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };
  
  if (!user) {
    return <div>사용자 정보를 불러오는 중...</div>;
  }
  
  return (
    <ProfileContainer>
      <ProfileSection>
        <SectionTitle>기본 정보</SectionTitle>
        {/* <Avatar>{user.username.charAt(0).toUpperCase()}</Avatar> */}
        <ProfileInfo>
          <InfoItem>
            <Label>아이디</Label>
            <Value>{user.username}</Value>
          </InfoItem>
          <InfoItem>
            <Label>가입일</Label>
            <Value>{new Date(user.createdAt || '').toLocaleDateString()}</Value>
          </InfoItem>
          <InfoItem>
            <Label>회원 등급</Label>
            <Value>{user.role || '일반 회원'}</Value>
          </InfoItem>
          <InfoItem>
            <Label>포인트</Label>
            <Value>{user.points?.toLocaleString() || 0} P</Value>
          </InfoItem>
        </ProfileInfo>
      </ProfileSection>
      
      <ProfileSection>
        <SectionTitle>프로필 수정</SectionTitle>
        <form onSubmit={handleSubmit}>
          <InfoItem>
            <Label>닉네임</Label>
            <Input 
              type="text" 
              value={nickname} 
              onChange={(e) => setNickname(e.target.value)}
              placeholder="닉네임을 입력하세요"
            />
          </InfoItem>
          <InfoItem>
            <Label>이메일</Label>
            <Input 
              type="email" 
              value={email} 
              onChange={(e) => setEmail(e.target.value)}
              placeholder="이메일을 입력하세요"
            />
          </InfoItem>
          
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}
          
          <Button type="submit" disabled={loading}>
            {loading ? '처리 중...' : '정보 수정'}
          </Button>
        </form>
      </ProfileSection>
    </ProfileContainer>
  );
};

export default Profile; 